import { useParams } from "react-router-dom";
import { isDragonstone, isSalesforceApplication } from "../helpers/url-params";
import { getAdobeEventTextName } from "../helpers/utils";
import { useAppSelector } from "../reduxStore/reduxHooks";
import { getConfigData } from "../reduxStore/slices/configSlice";
import * as adobeAnalytics from "../utility/adobe-analytics";
import { useGetJobDetails } from "./apis/useGetJobDetails";
import { useGetApplicationManageData } from "./apis/useGetApplicationManageData";
import { useGetCandidateData } from "./apis/useGetCandidateData";

type useSendJobCardAnalyticEvent = () => {
  sendNewHireVideoAnalyticsEvent: (eventName: string) => Promise<void>;
  sendFirstDayChangeAnalyticsEvent: (eventName: string) => Promise<void>;
  sendBadgePhotoPageAnalyticsEvent: (eventName: string) => Promise<void>;
  sendBadgePhotoEventAnalyticsEvent: (eventName: string) => Promise<void>;
  sendWithdrawApplicationAnalyticsEvent: (eventName: string, withdrawReason: string) => Promise<void>;
  sendDocumentsAnalyticsEvent: (eventName: string, option?: any, documentType?: string) => Promise<void>;
};

export const useSendAnalyticEvent: useSendJobCardAnalyticEvent = () => {
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();

  const jobDetails = useGetJobDetails();
  const applicationManageData = useGetApplicationManageData();
  const candidateData = useGetCandidateData();

  const sendFirstDayChangeAnalyticsEvent = async (eventName: string) => {
    if (!isSalesforceApplication(applicationId)) {
      await adobeAnalytics.addEventMetric(
        window,
        applicationId,
        eventName,
        candidateData,
        applicationManageData,
        jobDetails,
        {
          dragonstoneSchedule: {
            scheduleID: jobDetails?.scheduleId,
            jobID: requisitionId,
          },
          geocluster: {
            ID: jobDetails?.geoClusterId,
          },
        }
      );
    }
  };

  const sendWithdrawApplicationAnalyticsEvent = async (eventName: string, withdrawReason: string) => {
    await adobeAnalytics.addEventMetric(
      window,
      applicationId,
      eventName,
      candidateData,
      applicationManageData,
      jobDetails,
      {
        dragonstoneSchedule: {
          scheduleID: jobDetails?.scheduleId,
        },
        dragonstoneJob: {
          jobID: jobDetails?.jobId,
        },
        geocluster: {
          ID: jobDetails?.geoClusterId,
        },
        withdraw: {
          reason: withdrawReason,
        },
      }
    );
  };

  const sendDocumentsAnalyticsEvent = async (eventName: string, option?: any) => {
    await adobeAnalytics.addEventMetric(
      window,
      applicationId,
      eventName,
      candidateData,
      applicationManageData,
      jobDetails,
      {
        dragonstoneSchedule: {
          scheduleID: jobDetails?.scheduleId,
        },
        dragonstoneJob: {
          jobID: jobDetails?.jobId,
        },
        geocluster: {
          ID: jobDetails?.geoClusterId,
        },
        text: getAdobeEventTextName(eventName, option),
        option: option,
      }
    );
  };

  const sendNewHireVideoAnalyticsEvent = async (eventName: string) => {
    await adobeAnalytics.addPageLoadMetric(
      candidateData,
      applicationManageData,
      jobDetails,
      window,
      applicationId,
      eventName,
      {
        referrer: adobeAnalytics.interpretReferrer(document.referrer),
        jobId: requisitionId,
        isDragonstone: isDragonstone(requisitionId),
      }
    );
  };

  const sendBadgePhotoPageAnalyticsEvent = async (eventName: string) => {
    await adobeAnalytics.addPageLoadMetric(
      candidateData,
      applicationManageData,
      jobDetails,
      window,
      applicationId,
      eventName,
      {
        referrer: adobeAnalytics.interpretReferrer(document.referrer),
      }
    );
  };

  const sendBadgePhotoEventAnalyticsEvent = async (eventName: string) => {
    await adobeAnalytics.addEventMetric(
      window,
      applicationId,
      eventName,
      candidateData,
      applicationManageData,
      jobDetails,
      {
        referrer: adobeAnalytics.interpretReferrer(document.referrer),
      }
    );
  };

  return {
    sendNewHireVideoAnalyticsEvent,
    sendFirstDayChangeAnalyticsEvent,
    sendWithdrawApplicationAnalyticsEvent,
    sendDocumentsAnalyticsEvent,
    sendBadgePhotoPageAnalyticsEvent,
    sendBadgePhotoEventAnalyticsEvent,
  };
};
